import { useEffect, useState } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { getCategories, getIncome } from '../../api/client';

// A throwaway monthly budget sheet. Pure client-side: rows live in component
// state and persist to localStorage on every keystroke — no server, no save
// button, nothing touches the real budget. For deliberate planning use Plans.

const STORAGE_KEY = 'ledger:scratchpad';
const fmt = (n) => `${n < 0 ? '-' : ''}$${Math.abs(Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const rid = () => Math.random().toString(36).slice(2, 10);

// Rough per-month estimate for an income schedule. A scratchpad wants one simple
// monthly number to poke at, not payday-accurate math (that's what Plans do).
const MONTHLY_FACTOR = { monthly: 1, twiceMonthly: 2, biweekly: 2, weekly: 4 };

const loadSaved = () => {
  try {
    const d = JSON.parse(localStorage.getItem(STORAGE_KEY));
    if (d && Array.isArray(d.income) && Array.isArray(d.expenses)) return d;
  } catch (e) { /* corrupt/absent — fall through to the chooser */ }
  return null;
};

const blankRow = () => ({ id: rid(), name: '', amount: '' });
const sumRows = (rows) => rows.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);

const Scratchpad = () => {
  const { COLORS } = useTheme();
  const [sheet, setSheet]     = useState(loadSaved); // null → show the chooser
  const [seeding, setSeeding] = useState(false);

  // Persist as you type — "enter it and it's just there".
  useEffect(() => {
    if (sheet) localStorage.setItem(STORAGE_KEY, JSON.stringify(sheet));
  }, [sheet]);

  const seedBlank = () => setSheet({ income: [blankRow()], expenses: [blankRow()] });

  const seedFromBudget = async () => {
    setSeeding(true);
    try {
      const [catRes, incRes] = await Promise.all([getCategories(), getIncome()]);
      const income = (incRes.data || [])
        .filter((s) => MONTHLY_FACTOR[s.schedule_type])
        .map((s) => ({ id: rid(), name: s.name, amount: String(Math.round(parseFloat(s.amount || 0) * MONTHLY_FACTOR[s.schedule_type] * 100) / 100) }));
      const expenses = (catRes.data || [])
        .filter((c) => c.is_active && !c.exclude_from_budget && c.type !== 'cc_payment')
        .map((c) => ({ id: rid(), name: c.name, amount: String(parseFloat(c.amount || 0)) }));
      setSheet({
        income: income.length ? income : [blankRow()],
        expenses: expenses.length ? expenses : [blankRow()],
      });
    } catch (e) { /* leave chooser up */ }
    setSeeding(false);
  };

  const setCell = (side, rowId, field, value) =>
    setSheet((s) => ({ ...s, [side]: s[side].map((r) => (r.id === rowId ? { ...r, [field]: value } : r)) }));
  const addRow = (side) => setSheet((s) => ({ ...s, [side]: [...s[side], blankRow()] }));
  const delRow = (side, rowId) => setSheet((s) => ({ ...s, [side]: s[side].filter((r) => r.id !== rowId) }));
  const startOver = () => {
    if (!window.confirm('Wipe this scratch sheet?')) return;
    localStorage.removeItem(STORAGE_KEY);
    setSheet(null);
  };
  const reseed = () => {
    if (!window.confirm('Replace this sheet with your current budget?')) return;
    seedFromBudget();
  };

  const btn = (primary) => ({
    background: primary ? COLORS.accent : 'transparent',
    color: primary ? '#fff' : COLORS.textMuted,
    border: primary ? 'none' : `1px solid ${COLORS.border}`,
    borderRadius: 8, padding: '8px 14px', fontSize: 12.5, fontWeight: 700,
    cursor: 'pointer', fontFamily: 'inherit',
  });

  // ── chooser ────────────────────────────────────────────────────────────────
  if (!sheet) {
    return (
      <div style={{ textAlign: 'center', padding: '44px 16px', fontFamily: "'DM Sans', sans-serif" }}>
        <div style={{ fontSize: 40, marginBottom: 12 }}>🧮</div>
        <h3 style={{ color: COLORS.text, fontSize: 18, fontWeight: 800, margin: '0 0 6px' }}>Start a scratch sheet</h3>
        <p style={{ color: COLORS.textMuted, fontSize: 13, maxWidth: 400, margin: '0 auto 20px', lineHeight: 1.6 }}>
          A play-money monthly budget. Change any number and watch the total move —
          nothing here touches your real budget, and it's saved only on this device.
        </p>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
          <button onClick={seedFromBudget} disabled={seeding} style={{ ...btn(true), opacity: seeding ? 0.6 : 1 }}>
            {seeding ? 'Loading…' : 'Use my current budget'}
          </button>
          <button onClick={seedBlank} style={btn(false)}>Start blank</button>
        </div>
      </div>
    );
  }

  // ── sheet ──────────────────────────────────────────────────────────────────
  const incomeTotal  = sumRows(sheet.income);
  const expenseTotal = sumRows(sheet.expenses);
  const net = incomeTotal - expenseTotal;

  const cellInput = {
    background: 'transparent', border: '1px solid transparent', borderRadius: 6,
    color: COLORS.text, fontSize: 13, fontFamily: 'inherit', outline: 'none', padding: '7px 8px',
  };

  const section = (title, side, subtotal, subtotalColor) => (
    <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 12, overflow: 'hidden', marginBottom: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: COLORS.inputBg, borderBottom: `1px solid ${COLORS.border}` }}>
        <span style={{ color: COLORS.textMuted, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>{title}</span>
        <span style={{ color: subtotalColor || COLORS.text, fontFamily: 'monospace', fontSize: 13, fontWeight: 800 }}>{fmt(subtotal)}</span>
      </div>
      {sheet[side].map((r) => (
        <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 4, borderBottom: `1px solid ${COLORS.border}`, padding: '1px 8px' }}>
          <input
            value={r.name}
            placeholder="Name"
            onChange={(e) => setCell(side, r.id, 'name', e.target.value)}
            style={{ ...cellInput, flex: 1, minWidth: 0, fontWeight: 500 }}
          />
          <input
            value={r.amount}
            placeholder="0"
            inputMode="decimal"
            onChange={(e) => setCell(side, r.id, 'amount', e.target.value.replace(/[^0-9.-]/g, ''))}
            onFocus={(e) => e.target.select()}
            style={{ ...cellInput, width: 110, textAlign: 'right', fontFamily: 'monospace', fontWeight: 600 }}
          />
          <button onClick={() => delRow(side, r.id)} aria-label="Delete row" style={{ background: 'transparent', border: 'none', color: COLORS.textMuted, cursor: 'pointer', fontSize: 16, lineHeight: 1, padding: '0 4px' }}>×</button>
        </div>
      ))}
      <button onClick={() => addRow(side)} style={{ width: '100%', textAlign: 'left', background: 'transparent', border: 'none', color: COLORS.accent, cursor: 'pointer', fontFamily: 'inherit', fontSize: 12.5, fontWeight: 700, padding: '9px 14px' }}>
        + Add row
      </button>
    </div>
  );

  return (
    <div style={{ fontFamily: "'DM Sans', sans-serif" }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
        <span style={{ color: COLORS.textMuted, fontSize: 12 }}>Play money — saved on this device only.</span>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={reseed} disabled={seeding} style={btn(false)}>{seeding ? 'Loading…' : 'Re-seed from budget'}</button>
          <button onClick={startOver} style={{ ...btn(false), color: COLORS.red }}>Start over</button>
        </div>
      </div>

      {section('Income', 'income', incomeTotal, COLORS.green)}
      {section('Expenses', 'expenses', expenseTotal)}

      <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 12, padding: '13px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ color: COLORS.textMuted, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>Left over</span>
        <span style={{ color: net < 0 ? COLORS.red : COLORS.green, fontFamily: 'monospace', fontSize: 16, fontWeight: 800 }}>{fmt(net)}</span>
      </div>
    </div>
  );
};

export default Scratchpad;
